/**
 * Small pieces the install surfaces share: a browser button drawn inline, the mini
 * pictures of the browser's UI under a guide's steps, and Nib's pointer.
 */
import type { Glyph as GlyphName, Illustration, Pointer as PointerKind } from '../../pwa/guide.ts';
import { InstallIcon } from '../icons.tsx';

/** The address as the browser shows it. */
const host = () => (typeof location === 'undefined' ? 'sparrowword.com' : location.hostname.replace(/^www\./, ''));

/** One of the browser's buttons, drawn as a little white key ("Tap ⋯"). */
export function Glyph({ name }: { name: GlyphName }) {
	const label = { dots: 'the ⋯ button', share: 'the Share button', kebab: 'the ⋮ button', burger: 'the menu button' }[name];
	const I = { dots: InstallIcon.dots, share: InstallIcon.share, kebab: InstallIcon.kebab, burger: InstallIcon.burger }[name];
	return (
		<span className="inl" role="img" aria-label={label}>
			<I />
		</span>
	);
}

/** A step's text: lead, an inline glyph, the rest. */
export function StepText({ lead, glyph, rest }: { lead: string; glyph?: GlyphName | undefined; rest?: string | undefined }) {
	return (
		<>
			{lead}
			{glyph && (
				<>
					{' '}
					<Glyph name={glyph} />
				</>
			)}
			{rest ? ` ${rest}` : null}
		</>
	);
}

/** The bit of the browser to look for, drawn in the OS's style (so it reads as "not us"). */
export function Ill({ kind }: { kind: Illustration }) {
	switch (kind) {
		case 'bar_more':
			return (
				<span className="mb os" aria-hidden="true">
					<i className="c">
						<InstallIcon.back />
					</i>
					<i className="u">{host()}</i>
					<i className="c hot">
						<InstallIcon.dots />
					</i>
				</span>
			);
		case 'bar_share':
			return (
				<span className="mb tool os" aria-hidden="true">
					<i className="c">
						<InstallIcon.back />
					</i>
					<i className="c">
						<InstallIcon.fwd />
					</i>
					<i className="c hot">
						<InstallIcon.share />
					</i>
					<i className="c">
						<InstallIcon.book />
					</i>
					<i className="c">
						<InstallIcon.copy />
					</i>
				</span>
			);
		case 'bar_ipad':
			return (
				<span className="mb tool os" aria-hidden="true">
					<i className="u">{host()}</i>
					<i className="c hot">
						<InstallIcon.share />
					</i>
					<i className="c">
						<InstallIcon.add />
					</i>
				</span>
			);
		case 'share_rows':
			return (
				<span className="mrows os" aria-hidden="true">
					<span>
						Share <InstallIcon.share />
					</span>
					<span className="hot">
						Add to Home Screen <InstallIcon.add />
					</span>
				</span>
			);
		case 'add_rows':
			return (
				<span className="mrows os" aria-hidden="true">
					<span className="hot">
						Add to Home Screen <InstallIcon.add />
					</span>
				</span>
			);
		case 'web_app_toggle':
			return (
				<span className="tg-row" aria-hidden="true">
					<span className="mrows os">
						<span>
							Open as Web App <i className="tog" />
						</span>
					</span>
					<span className="addchip os">Add</span>
				</span>
			);
		case 'add_button':
			return (
				<span className="tg-row" aria-hidden="true">
					<span className="addchip os">Add</span>
				</span>
			);
	}
}

/** Nib's vermilion wedge, bobbing at the browser's button just off the edge of our page. */
export function Pointer({ at }: { at: PointerKind }) {
	if (!at) return null;
	const rotate = at === 'bottom_right' ? 45 : at === 'bottom_centre' ? 90 : -45;
	return (
		<div className={`pointer pointer-${at}`} aria-hidden="true">
			<svg viewBox="0 0 24 24" className="bob">
				<path d="M2 9.2h10.6V3.6L22 12l-9.4 8.4v-5.6H2z" transform={`rotate(${rotate} 12 12)`} />
			</svg>
		</div>
	);
}
